import { all, call, put, takeLatest, takeEvery } from 'redux-saga/effects';
import { httpRequestsOnLoadActions } from '../http_requests_on_load';
import { httpRequestsOnSuccessActions } from '../http_requests_on_success';
import { httpRequestsOnErrorsActions } from '../http_requests_on_errors';
import { documentVersionService } from './documentVersion.service';
import {
    GET_DOCUMENT_VERSIONS,
    GET_DOCUMENT_VERSIONS_SUCCESS,
    GET_DRAFT_STATE,
    GET_DRAFT_STATE_SUCCESS,
    GET_DOCUMENT_VERSION_DETAIL,
    GET_DOCUMENT_VERSION_DETAIL_SUCCESS,
    RECONSTRUCT_DOCUMENT_VERSION,
    RECONSTRUCT_DOCUMENT_VERSION_SUCCESS,
    CAPTURE_DOCUMENT_CHANGES,
    DISCARD_DOCUMENT_DRAFT,
    DISCARD_DOCUMENT_DRAFT_SUCCESS,
    RESTORE_DOCUMENT_VERSION,
    RESTORE_DOCUMENT_VERSION_SUCCESS,
} from './documentVersion.types';

// ── Read operations ──────────────────────────────────────────────────────────

function* getDocumentVersions({ payload, type }) {
    yield put(httpRequestsOnErrorsActions.removeError(type));
    yield put(httpRequestsOnLoadActions.appendLoading(type));
    try {
        const res = yield call(documentVersionService.listVersions, payload?.params);
        yield put({ type: GET_DOCUMENT_VERSIONS_SUCCESS, payload: res?.data?.data || res?.data || [] });
        yield put(httpRequestsOnLoadActions.removeLoading(type));
        yield put(httpRequestsOnErrorsActions.removeError(type));
    } catch (err) {
        yield put(httpRequestsOnLoadActions.removeLoading(type));
        yield put(httpRequestsOnErrorsActions.appendError(type, err?.data?.message));
    }
}

function* getDraftState({ payload, type }) {
    yield put(httpRequestsOnErrorsActions.removeError(type));
    yield put(httpRequestsOnLoadActions.appendLoading(type));
    try {
        const res = yield call(documentVersionService.getDraftState, payload?.params);
        const draft = res?.data?.data || res?.data;
        yield put({ type: GET_DRAFT_STATE_SUCCESS, payload: draft?.hasDraft ? draft : null });
        yield put(httpRequestsOnLoadActions.removeLoading(type));
        yield put(httpRequestsOnErrorsActions.removeError(type));
    } catch (err) {
        yield put(httpRequestsOnLoadActions.removeLoading(type));
        yield put(httpRequestsOnErrorsActions.appendError(type, err?.data?.message));
    }
}

function* getDocumentVersionDetail({ payload, type }) {
    yield put(httpRequestsOnErrorsActions.removeError(type));
    yield put(httpRequestsOnLoadActions.appendLoading(type));
    try {
        const res = yield call(documentVersionService.getVersion, payload?.id);
        const doc = res?.data?.data || res?.data;
        yield put({
            type: GET_DOCUMENT_VERSION_DETAIL_SUCCESS,
            payload: { id: payload?.id, changes: doc?.changes || [] },
        });
        yield put(httpRequestsOnLoadActions.removeLoading(type));
        yield put(httpRequestsOnErrorsActions.removeError(type));
    } catch (err) {
        yield put({
            type: GET_DOCUMENT_VERSION_DETAIL_SUCCESS,
            payload: { id: payload?.id, changes: [] },
        });
        yield put(httpRequestsOnLoadActions.removeLoading(type));
        yield put(httpRequestsOnErrorsActions.appendError(type, err?.data?.message));
    }
}

function* reconstructDocumentVersion({ payload, type }) {
    yield put(httpRequestsOnErrorsActions.removeError(type));
    yield put(httpRequestsOnSuccessActions.removeSuccess(type));
    yield put(httpRequestsOnLoadActions.appendLoading(type));
    try {
        const res = yield call(documentVersionService.reconstruct, payload?.params);
        yield put({ type: RECONSTRUCT_DOCUMENT_VERSION_SUCCESS, payload: res?.data?.data || res?.data });
        yield put(httpRequestsOnLoadActions.removeLoading(type));
        yield put(httpRequestsOnSuccessActions.appendSuccess(type));
    } catch (err) {
        yield put(httpRequestsOnLoadActions.removeLoading(type));
        yield put(httpRequestsOnErrorsActions.appendError(type, err?.data?.message));
    }
}

// ── Write operations ─────────────────────────────────────────────────────────

/** Fire-and-forget: no loading/success tracking — called on blur, must never block UI. */
function* captureDocumentChanges({ payload }) {
    try {
        yield call(documentVersionService.captureChanges, payload?.body);

        // Refresh draft state so the UI reflects the updated change count
        const params = {
            resourceModel: payload?.body?.resourceModel,
            resourceId: payload?.body?.resourceId,
        };
        const res = yield call(documentVersionService.getDraftState, params);
        const draft = res?.data?.data || res?.data;
        yield put({ type: GET_DRAFT_STATE_SUCCESS, payload: draft?.hasDraft ? draft : null });
    } catch (err) {
        /* versioning must never block editing */
    }
}

function* discardDocumentDraft({ payload, type }) {
    yield put(httpRequestsOnErrorsActions.removeError(type));
    yield put(httpRequestsOnSuccessActions.removeSuccess(type));
    yield put(httpRequestsOnLoadActions.appendLoading(type));
    try {
        yield call(documentVersionService.discardDraft, payload?.params);
        yield put({ type: DISCARD_DOCUMENT_DRAFT_SUCCESS });
        yield put(httpRequestsOnLoadActions.removeLoading(type));
        yield put(httpRequestsOnSuccessActions.appendSuccess(type));
    } catch (err) {
        yield put(httpRequestsOnLoadActions.removeLoading(type));
        yield put(httpRequestsOnErrorsActions.appendError(type, err?.data?.message));
    }
}

function* restoreDocumentVersion({ payload, type }) {
    yield put(httpRequestsOnErrorsActions.removeError(type));
    yield put(httpRequestsOnSuccessActions.removeSuccess(type));
    yield put(httpRequestsOnLoadActions.appendLoading(type));
    try {
        const res = yield call(documentVersionService.restore, payload?.body);
        const data = res?.data?.data || res?.data;
        yield put({ type: RESTORE_DOCUMENT_VERSION_SUCCESS, payload: data });

        // Auto re-fetch versions + draft after restore
        const params = {
            resourceModel: payload?.body?.resourceModel,
            resourceId: payload?.body?.resourceId,
        };
        const [listRes, draftRes] = yield all([
            call(documentVersionService.listVersions, params),
            call(documentVersionService.getDraftState, params),
        ]);
        yield put({ type: GET_DOCUMENT_VERSIONS_SUCCESS, payload: listRes?.data?.data || listRes?.data || [] });
        const draft = draftRes?.data?.data || draftRes?.data;
        yield put({ type: GET_DRAFT_STATE_SUCCESS, payload: draft?.hasDraft ? draft : null });

        yield put(httpRequestsOnLoadActions.removeLoading(type));
        yield put(httpRequestsOnSuccessActions.appendSuccess(type, data));
    } catch (err) {
        yield put(httpRequestsOnLoadActions.removeLoading(type));
        yield put(httpRequestsOnErrorsActions.appendError(type, err?.data?.message));
    }
}

// ── Watcher ──────────────────────────────────────────────────────────────────

export const watchDocumentVersion = function* watchDocumentVersionSaga() {
    yield takeLatest(GET_DOCUMENT_VERSIONS, getDocumentVersions);
    yield takeLatest(GET_DRAFT_STATE, getDraftState);
    yield takeLatest(GET_DOCUMENT_VERSION_DETAIL, getDocumentVersionDetail);
    yield takeLatest(RECONSTRUCT_DOCUMENT_VERSION, reconstructDocumentVersion);
    yield takeEvery(CAPTURE_DOCUMENT_CHANGES, captureDocumentChanges);
    yield takeLatest(DISCARD_DOCUMENT_DRAFT, discardDocumentDraft);
    yield takeLatest(RESTORE_DOCUMENT_VERSION, restoreDocumentVersion);
};
